import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { JADE_DARK, JADE_LIGHT } from '../../context/ThemeContext';
import Magnet from './Magnet';
import './LandingNavbar.css';

const EASE = 'power3.out';
const LINKS = [
  // 'Features' (#features) retired 2026-10-23 along with the landing page's
  // feature grid — the page leads with the community and mentorship now.
  { label: 'Community', href: '#community' },
  { label: 'Mentorship', href: '/mentorship' },
  // '#pricing' retired 2026-10-23 with the landing page's plans table; the
  // standalone /pricing route still renders it.
  { label: 'Pricing', href: '/pricing' },
  { label: 'About', href: '/about' },
];

// Hash anchors and mailto/external links use a plain <a> (no client-side
// route to take over); anything else — like /about — goes through router
// Link so it doesn't force a full page reload.
const isRouterHref = (href) => href.startsWith('/');

// ── LandingNavbar ────────────────────────────────────────────────────────────
// Floating pill nav for the marketing page — adapted from React Bits'
// PillNav (https://reactbits.dev/components/pill-nav): each link is its own
// pill, and on hover a gold circle scales up out of the bottom edge while the
// label crossfades to a dark-on-gold label riding on top of it. Self-contained
// — it tracks its own scroll position and morphs from a transparent bar over
// the dark Hero into a glass capsule once Parchment content scrolls beneath it.
export default function LandingNavbar({ onSignIn }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const circleRefs = useRef([]);
  const tlRefs = useRef([]);
  const activeTweenRefs = useRef([]);
  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navRef = useRef(null);
  // Always-latest handle to `layout` (defined further down) so the
  // mount-only entrance effect above can call it without a declaration-order
  // problem — plain assignment during render, not inside an effect, is the
  // standard way to keep a ref pointed at the newest closure.
  const layoutRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Materialize on load: the logo, each pill and the CTA start scattered and
  // dissolved into the Hero — as if they were just more nodes drifting in
  // the nerve canvas behind them — then converge into the assembled bar.
  // Runs once; useLayoutEffect so the scattered start state is painted
  // before the browser's first frame (no flash of the assembled navbar).
  //
  // Deliberately no `scale` here: each pill's hover-circle geometry (see
  // `layout` below) is measured off the *rendered* pill size via
  // getBoundingClientRect the moment this component mounts. Scaling the
  // `<li>` down for the scatter would make that first measurement read a
  // shrunken pill, baking a wrong (too-small, off-center) circle in for the
  // rest of the page's life — that's what caused the hover-circle to blow
  // out above the "About" pill. Position/opacity/blur don't affect layout
  // size, so they're safe to animate here.
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const nav = navRef.current;
    if (!nav) return;
    const targets = nav.querySelectorAll('.mn-nav-logo, .mn-pill-list > li, .mn-nav-cta, .mn-nav-burger');
    if (!targets.length) return;

    gsap.set(targets, {
      opacity: 0, filter: 'blur(5px)',
      x: () => gsap.utils.random(-60, 60),
      y: () => gsap.utils.random(-36, 36),
    });
    const tween = gsap.to(targets, {
      opacity: 1, x: 0, y: 0, filter: 'blur(0px)',
      duration: 1.1, ease: 'elastic.out(1, 0.7)',
      stagger: { each: 0.06, from: 'random' },
      delay: 0.2, overwrite: true,
      // Hand transform/filter back to plain CSS once settled, so the
      // logo's own :hover scale (and anything else CSS-driven) still works
      // afterward instead of being pinned by a leftover inline style. Also
      // re-run the hover-circle layout once everything has actually
      // settled at its final size, as a safety net.
      clearProps: 'transform,filter',
      onComplete: () => layoutRef.current?.(),
    });
    return () => tween.kill();
  }, []);

  // Scroll-progress hairline pinned to the very top of the viewport.
  const progressRef = useRef(null);
  useEffect(() => {
    const setter = progressRef.current && gsap.quickTo(progressRef.current, 'scaleX', { duration: 0.15, ease: 'none' });
    const onScroll = () => {
      if (!setter) return;
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setter(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lay out each pill's hover-circle geometry (ported from React Bits'
  // PillNav.layout()) so the circle's transform-origin sits exactly on the
  // pill's bottom edge and it scales up to just cover the pill.
  const layout = useCallback(() => {
    circleRefs.current.forEach((circle, i) => {
      if (!circle?.parentElement) return;
      const pill = circle.parentElement;
      const { width: w, height: h } = pill.getBoundingClientRect();
      const R = ((w * w) / 4 + h * h) / (2 * h);
      const D = Math.ceil(2 * R) + 2;
      const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
      const originY = D - delta;

      circle.style.width = `${D}px`;
      circle.style.height = `${D}px`;
      circle.style.bottom = `-${delta}px`;
      gsap.set(circle, { xPercent: -50, scale: 0, transformOrigin: `50% ${originY}px` });

      const label = pill.querySelector('.mn-pill-label');
      const hoverLabel = pill.querySelector('.mn-pill-label-hover');
      if (label) gsap.set(label, { y: 0 });
      if (hoverLabel) gsap.set(hoverLabel, { y: h + 12, opacity: 0 });

      tlRefs.current[i]?.kill();
      const tl = gsap.timeline({ paused: true });
      tl.to(circle, { scale: 1.2, xPercent: -50, duration: 2, ease: EASE, overwrite: 'auto' }, 0);
      if (label) tl.to(label, { y: -(h + 8), duration: 2, ease: EASE, overwrite: 'auto' }, 0);
      if (hoverLabel) {
        gsap.set(hoverLabel, { y: Math.ceil(h + 100), opacity: 0 });
        tl.to(hoverLabel, { y: 0, opacity: 1, duration: 2, ease: EASE, overwrite: 'auto' }, 0);
      }
      tlRefs.current[i] = tl;
    });
  }, []);
  layoutRef.current = layout;

  useEffect(() => {
    layout();
    window.addEventListener('resize', layout);
    document.fonts?.ready?.then(layout).catch(() => {});
    return () => window.removeEventListener('resize', layout);
  }, [layout]);

  const handleEnter = (i) => {
    const tl = tlRefs.current[i];
    if (!tl) return;
    activeTweenRefs.current[i]?.kill();
    activeTweenRefs.current[i] = tl.tweenTo(tl.duration(), { duration: 0.3, ease: EASE, overwrite: 'auto' });
  };
  const handleLeave = (i) => {
    const tl = tlRefs.current[i];
    if (!tl) return;
    activeTweenRefs.current[i]?.kill();
    activeTweenRefs.current[i] = tl.tweenTo(0, { duration: 0.25, ease: EASE, overwrite: 'auto' });
  };

  const toggleMobile = () => {
    const next = !mobileOpen;
    setMobileOpen(next);
    const lines = hamburgerRef.current?.querySelectorAll('.mn-burger-line');
    if (lines) {
      gsap.to(lines[0], { rotation: next ? 45 : 0, y: next ? 3 : 0, duration: 0.3, ease: EASE });
      gsap.to(lines[1], { rotation: next ? -45 : 0, y: next ? -3 : 0, duration: 0.3, ease: EASE });
    }
    const menu = mobileMenuRef.current;
    if (!menu) return;
    if (next) {
      gsap.set(menu, { visibility: 'visible' });
      gsap.fromTo(menu, { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.3, ease: EASE });
      gsap.fromTo(menu.querySelectorAll('.mn-mobile-stagger'),
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.35, stagger: 0.05, ease: EASE, delay: 0.05 });
    } else {
      gsap.to(menu, { opacity: 0, y: -8, duration: 0.2, ease: EASE, onComplete: () => gsap.set(menu, { visibility: 'hidden' }) });
    }
  };

  const isHeaderOverHero = !scrolled;
  const HC = isHeaderOverHero ? JADE_DARK.colors : JADE_LIGHT.colors;
  const nerveRgb = isHeaderOverHero ? JADE_DARK.nerveRgb : JADE_LIGHT.nerveRgb;
  const surfaceRgb = isHeaderOverHero ? JADE_DARK.surfaceRgb : JADE_LIGHT.surfaceRgb;

  const navStyle = {
    background: scrolled ? `rgba(${surfaceRgb},${isHeaderOverHero ? 0.75 : 0.85})` : `rgba(${nerveRgb},0.055)`,
    border: `1px solid ${scrolled ? HC.border : `rgba(${nerveRgb},0.14)`}`,
    boxShadow: scrolled
      ? (isHeaderOverHero ? '0 8px 40px rgba(0,0,0,0.35)' : '0 8px 40px rgba(10,24,18,0.14)')
      : `0 4px 28px rgba(${nerveRgb},0.08)`,
    backdropFilter: 'blur(18px) saturate(180%)',
    WebkitBackdropFilter: 'blur(18px) saturate(180%)',
    '--mn-action': HC.action,
    '--mn-action-lo': HC.actionLo,
    '--mn-action-hi': HC.actionHi,
    '--mn-pill-bg': 'transparent',
    '--mn-pill-text': HC.textMuted,
    '--mn-signin-text': isHeaderOverHero ? '#fff' : HC.bg,
    '--mn-burger-bg': `rgba(${nerveRgb},0.1)`,
  };

  return (
    <>
      <div ref={progressRef} className="mn-nav-progress" style={{ '--mn-action-lo': HC.actionLo, '--mn-action': HC.action, '--mn-action-hi': HC.actionHi }} />

      <div className="mn-nav-wrap">
        <nav ref={navRef} className="mn-nav" style={navStyle} aria-label="Primary">
          <Link to="/" className="mn-nav-logo" aria-label="MarketNerve home">
            <img src="/wordmark.png" alt="MarketNerve" />
          </Link>

          <div className="mn-pill-items">
            <ul className="mn-pill-list" role="menubar">
              {LINKS.map((item, i) => {
                const pillInner = (
                  <>
                    <span className="mn-hover-circle" aria-hidden="true" ref={el => { circleRefs.current[i] = el; }} />
                    <span className="mn-label-stack">
                      <span className="mn-pill-label">{item.label}</span>
                      <span className="mn-pill-label-hover" aria-hidden="true">{item.label}</span>
                    </span>
                  </>
                );
                const pillProps = {
                  role: 'menuitem', className: 'mn-pill',
                  onMouseEnter: () => handleEnter(i), onMouseLeave: () => handleLeave(i),
                };
                return (
                  <li key={item.href} role="none">
                    {isRouterHref(item.href)
                      ? <Link to={item.href} {...pillProps}>{pillInner}</Link>
                      : <a href={item.href} {...pillProps}>{pillInner}</a>}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mn-nav-cta">
            <Magnet padding={70} strength={7}>
              <button onClick={onSignIn} className="mn-nav-signin">
                Sign In
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </Magnet>
          </div>

          <button className="mn-nav-burger" onClick={toggleMobile} aria-label="Toggle menu" aria-expanded={mobileOpen} ref={hamburgerRef} style={{ '--mn-burger-bg': `rgba(${nerveRgb},0.1)`, background: `rgba(${nerveRgb},0.1)` }}>
            <span className="mn-burger-line" style={{ background: HC.text }} />
            <span className="mn-burger-line" style={{ background: HC.text }} />
          </button>
        </nav>
      </div>

      <div
        ref={mobileMenuRef}
        className="mn-mobile-menu"
        style={{ background: `rgba(${surfaceRgb},0.97)`, border: `1px solid ${HC.border}`, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      >
        <ul className="mn-mobile-menu-list">
          {LINKS.map(item => (
            <li key={item.href} className="mn-mobile-stagger">
              {isRouterHref(item.href)
                ? <Link to={item.href} className="mn-mobile-menu-link" style={{ color: HC.text }} onClick={() => toggleMobile()}>{item.label}</Link>
                : <a href={item.href} className="mn-mobile-menu-link" style={{ color: HC.text }} onClick={() => toggleMobile()}>{item.label}</a>}
            </li>
          ))}
        </ul>
        <button
          className="mn-mobile-stagger mn-mobile-signin"
          style={{ background: HC.action, color: '#fff' }}
          onClick={() => { toggleMobile(); onSignIn?.(); }}
        >
          Sign In
        </button>
      </div>
    </>
  );
}
